import { useEffect, useMemo, useState } from 'preact/hooks';
import { DEFAULT_RULES, type RulesConfig } from '../engine';
import { MAX_NAME_LENGTH, MAX_SEATS, MIN_SEATS_TO_START } from '../shared/protocol';
import type { GameClient } from './game-client';
import { useRoom, type RoomControls, type RoomState } from './room';
import { RulesForm } from './rules-form';
import { Table } from './table';

const NAME_KEY = 'cyp.name';

function readName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Private mode; the name just won't be remembered.
  }
}

type Room = RoomState & RoomControls;

function ConnectionBanner({ status }: { status: RoomState['status'] }) {
  if (status === 'open') return null;
  return (
    <div class="banner" data-testid="connection">
      {status === 'connecting' ? 'Connecting…' : status === 'reconnecting' ? 'Reconnecting…' : 'Disconnected.'}
    </div>
  );
}

function ShareLink({ code }: { code: string }) {
  const url = `${location.origin}/r/${code}`;
  const [copied, setCopied] = useState(false);
  return (
    <div class="row wrap">
      <span class="muted" data-testid="share-link">
        {url}
      </span>
      <button
        type="button"
        class="small secondary"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
          } catch {
            // Clipboard blocked; the link is still selectable.
          }
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function Lobby({ code, room }: { code: string; room: Room }) {
  const [name, setName] = useState(readName);
  const [rules, setRules] = useState<RulesConfig>(DEFAULT_RULES);
  const seats = room.room?.seats ?? [];
  const isHost = room.seat !== null && room.seat === room.room?.host;

  // Someone opening the link just needs to say who they are; the room details are for the table.
  if (room.seat === null) {
    return (
      <div class="panel lobby join" data-testid="join-panel">
        <h2>Join Corwin's Call Your Partner</h2>
        {seats.length < MAX_SEATS ? (
          <form
            class="join-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              saveName(name.trim());
              room.join(name.trim());
            }}
          >
            <input
              value={name}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
              maxLength={MAX_NAME_LENGTH}
              placeholder="Your name"
              aria-label="Your name"
              autoFocus
              data-testid="name-input"
            />
            <button type="submit" class="primary" data-testid="join-room" disabled={!name.trim()}>
              Join
            </button>
          </form>
        ) : (
          <p class="muted">This room is full.</p>
        )}
        {seats.length > 0 && (
          <p class="muted small" data-testid="players">
            Already here: {seats.map((s) => `${s.bot ? '🤖 ' : ''}${s.name}`).join(', ')}
          </p>
        )}
        {room.notice && <p class="error">{room.notice.text}</p>}
      </div>
    );
  }

  return (
    <div class="panel lobby">
      <h2>Room {code}</h2>
      <p class="muted">Send this link to everyone who's playing:</p>
      <ShareLink code={code} />
      <ol class="players" data-testid="players">
        {seats.map((s, i) => (
          <li key={i}>
            {s.name}
            {s.bot && <span class="muted"> 🤖</span>}
            {i === room.room?.host && <span class="muted"> (host)</span>}
            {i === room.seat && <span class="muted"> (you)</span>}
            {!s.connected && <span class="offline"> (away)</span>}
            {isHost && s.bot && (
              <button class="small link" data-testid={`remove-bot-${i}`} onClick={() => room.removeBot(i)}>
                Remove
              </button>
            )}
            {isHost && !s.bot && !s.connected && (
              <button class="small link" data-testid={`remove-${i}`} onClick={() => room.release(i)}>
                Remove
              </button>
            )}
          </li>
        ))}
      </ol>
      {isHost ? (
        <>
          <button class="secondary" data-testid="add-bot" disabled={seats.length >= MAX_SEATS} onClick={() => room.addBot()}>
            Add a computer player
          </button>
          <RulesForm rules={rules} onChange={setRules} />
          <div class="row between">
            <button
              class="primary"
              data-testid="start-game"
              disabled={seats.length < MIN_SEATS_TO_START}
              onClick={() => {
                const { handLimit: _fixed, ...chosen } = rules;
                room.start(chosen);
              }}
            >
              Deal
            </button>
            <button class="link dark" data-testid="leave-room" onClick={() => room.leave()}>
              Leave
            </button>
          </div>
        </>
      ) : (
        <div class="row between">
          <span class="muted" data-testid="waiting-host">
            Waiting for {seats[room.room?.host ?? 0]?.name ?? 'the host'} to deal…
          </span>
          <button class="link dark" data-testid="leave-room" onClick={() => room.leave()}>
            Leave
          </button>
        </div>
      )}
      {room.notice && <p class="error">{room.notice.text}</p>}
    </div>
  );
}

/** Mid-game seat handoff: the host covers an away seat; visitors take over open or computer seats. */
function SeatsPanel({ room }: { room: Room }) {
  const seats = room.room?.seats ?? [];
  const isHost = room.seat !== null && room.seat === room.room?.host;
  const away = isHost ? seats.flatMap((s, i) => (!s.bot && !s.connected && !s.open ? [i] : [])) : [];
  const takeable = room.seat === null ? seats.flatMap((s, i) => (s.open || s.bot ? [i] : [])) : [];
  if (away.length === 0 && takeable.length === 0) return null;
  return (
    <div class="panel seats-panel" data-testid="seats-panel">
      {away.map((i) => (
        <div class="row wrap" key={`away-${i}`}>
          <span>{seats[i]!.name} is away.</span>
          <button class="small secondary" data-testid={`to-bot-${i}`} onClick={() => room.release(i, true)}>
            Let a computer play for them
          </button>
          <button class="small secondary" data-testid={`release-${i}`} onClick={() => room.release(i)}>
            Open the seat
          </button>
        </div>
      ))}
      {takeable.map((i) => (
        <div class="row wrap" key={`take-${i}`}>
          <span>
            {seats[i]!.name}'s seat is {seats[i]!.bot ? 'being played by a computer' : 'open'}.
          </span>
          <button class="small primary" data-testid={`claim-${i}`} onClick={() => room.claim(i)}>
            Play as {seats[i]!.name}
          </button>
        </div>
      ))}
    </div>
  );
}

export function OnlineRoom({ code }: { code: string }) {
  const room = useRoom(code);
  const { game } = room;
  const isHost = room.seat !== null && room.seat === room.room?.host;

  const client = useMemo<GameClient | null>(
    () =>
      game && {
        view: game.view,
        seat: room.seat,
        legal: game.legal,
        names: room.room?.seats.map((s) => s.name) ?? [],
        bots: room.room?.seats.map((s) => s.bot) ?? [],
        log: room.log,
        act: (action) => {
          room.act(action);
          return null;
        },
        hotSeat: false,
        notice: room.notice,
        ...(isHost ? { undo: room.undo } : {}),
      },
    [game, room.seat, room.room, room.log, room.notice, isHost],
  );

  const yourMove = !!game && game.view.phase.kind !== 'gameOver' && (game.legal.length > 0 || needsYou(game.view, room.seat));
  useEffect(() => {
    document.title = yourMove ? "● Your move — Corwin's Call Your Partner" : "Corwin's Call Your Partner";
    return () => {
      document.title = "Corwin's Call Your Partner";
    };
  }, [yourMove]);

  if (room.fatal) {
    return (
      <div class="panel" data-testid="fatal">
        <h2>{room.fatal === 'room not found' ? 'Room not found' : 'Something went wrong'}</h2>
        <p class="muted">{room.fatal === 'room not found' ? `There's no room ${code}. It may have expired.` : room.fatal}</p>
        <a class="button primary" href="/">
          Back to start
        </a>
      </div>
    );
  }

  const finished = room.room?.status === 'finished';
  return (
    <>
      <ConnectionBanner status={room.status} />
      {room.info && <div class="toast info">{room.info.text}</div>}
      {client && room.room?.status !== 'lobby' ? (
        <>
          {finished && (
            <div class="panel" data-testid="rematch">
              {isHost ? (
                <button class="primary" data-testid="play-again" onClick={() => {
                  const { handLimit: _fixed, ...rules } = room.room?.rules ?? {};
                  room.start(rules);
                }}>
                  Play again with the same table
                </button>
              ) : (
                <span class="muted">Waiting for the host to deal another game…</span>
              )}
            </div>
          )}
          <SeatsPanel room={room} />
          {room.seat === null && (
            <div class="panel" data-testid="spectating">
              <p class="muted">You're watching. Hands are hidden.</p>
            </div>
          )}
          <Table client={client} />
        </>
      ) : (
        <Lobby code={code} room={room} />
      )}
    </>
  );
}

/** `give` and `keep` aren't in `legal`, so check the view for those too. */
function needsYou(view: GameClient['view'], seat: number | null): boolean {
  if (seat === null) return false;
  const p = view.phase;
  return (p.kind === 'pass' && view.hand.bidder === seat) || (p.kind === 'trim' && !!p.pending[seat]);
}
